// frontend/src/pages/InstructorDashboard.jsx
import React, { useState, useEffect } from 'react';
import { getQuestions, getAnalytics, getCurriculumGaps } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

// Absolutely positioned bar chart for reliable display
const AbsoluteBarChart = ({ data, title, getLabel, getValue }) => {
  // If no data or empty data, show a message
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <h2 className="text-lg font-medium mb-2">{title}</h2>
        <p className="text-gray-500 text-center py-4">No data available</p>
      </div>
    );
  }

  // Extract values
  const values = data.map(item => getValue(item));
  const maxValue = Math.max(...values);
  
  // Chart dimensions
  const chartHeight = 240;
  const barWidth = 40;
  const barSpacing = 20;
  const labelHeight = 40;
  
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <h2 className="text-lg font-medium p-4 border-b">{title}</h2>
      <div className="p-4 overflow-x-auto">
        <div style={{ 
          position: 'relative', 
          height: `${chartHeight + labelHeight}px`,
          minWidth: `${data.length * (barWidth + barSpacing)}px`
        }}>
          {/* Bars */}
          {data.map((item, index) => {
            const value = getValue(item);
            const label = getLabel(item);
            
            // Calculate pixel height based on value - using direct pixel calculation
            const barHeight = maxValue > 0 
              ? Math.max(Math.round((value / maxValue) * (chartHeight - 20)), value > 0 ? 4 : 0)
              : 0;
            
            return (
              <div 
                key={index} 
                style={{
                  position: 'absolute',
                  left: `${index * (barWidth + barSpacing)}px`,
                  bottom: `${labelHeight}px`,
                  width: `${barWidth}px`,
                  height: `${barHeight}px`,
                  backgroundColor: '#4f46e5', // indigo-600
                  borderTopLeftRadius: '0.25rem',
                  borderTopRightRadius: '0.25rem',
                }}
              />
            );
          })}
          
          {/* Labels */}
          {data.map((item, index) => {
            const value = getValue(item);
            const label = getLabel(item);
            
            return (
              <div 
                key={`label-${index}`} 
                style={{
                  position: 'absolute',
                  left: `${index * (barWidth + barSpacing)}px`,
                  bottom: '0',
                  width: `${barWidth + barSpacing}px`,
                  textAlign: 'center',
                }}
              >
                <div className="mt-1 text-xs font-medium truncate">
                  {label}
                </div>
                <div className="text-xs text-gray-500">
                  {value}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const InstructorDashboard = () => {
  const [questions, setQuestions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(7); // Default to 7 days
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const questionsPerPage = 10;
  const [activeTab, setActiveTab] = useState('questions'); // 'questions', 'analytics', 'gaps'
  const [curriculumGaps, setCurriculumGaps] = useState([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    loadQuestions();
    loadAnalytics();
  }, [timeframe, currentPage, searchTerm]);
  
  const loadQuestions = async () => {
    try {
      setLoading(true);
      
      const offset = (currentPage - 1) * questionsPerPage;
      const response = await getQuestions(questionsPerPage, offset, timeframe, searchTerm);
      
      setQuestions(response.questions);
      setTotalQuestions(response.total);
    } catch (error) {
      console.error('Error loading questions:', error);
      
      // If unauthorized, log out and redirect to login
      if (error.message.includes('Not authenticated')) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadAnalytics = async () => {
    try {
      const analyticsData = await getAnalytics(timeframe);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };
  
  const loadCurriculumGaps = async () => {
    try {
      setGapsLoading(true);
      const gapsData = await getCurriculumGaps(timeframe);
      setCurriculumGaps(gapsData);
    } catch (error) {
      console.error('Error loading curriculum gaps:', error);
    } finally {
      setGapsLoading(false);
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    if (tab === 'gaps' && curriculumGaps.length === 0) {
      loadCurriculumGaps();
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    loadQuestions(); // Explicitly load questions
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(selectedQuestion?.id === question.id ? null : question);
  };
  
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  
  if (loading && !questions.length && activeTab === 'questions') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-indigo-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-semibold">Instructor Dashboard</h1>
          <div className="flex items-center">
            <span className="mr-4 text-sm">{localStorage.getItem('instructor_name') || 'Instructor'}</span>
            <button 
              onClick={handleLogout}
              className="text-sm bg-indigo-700 px-3 py-1 rounded hover:bg-indigo-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      
      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto flex">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'questions' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-indigo-500'}`}
            onClick={() => handleTabChange('questions')}
          >
            Student Questions
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'analytics' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-indigo-500'}`}
            onClick={() => handleTabChange('analytics')}
          >
            Analytics
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'gaps' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-indigo-500'}`}
            onClick={() => handleTabChange('gaps')}
          >
            Curriculum Gaps
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <main className="container mx-auto py-6 px-4">
        {/* Time frame selector (common to all tabs) */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-medium mb-2">Time Range</h2>
          <div className="flex space-x-2">
            {[7, 14, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setTimeframe(days)}
                className={`px-3 py-1 rounded ${
                  timeframe === days
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
        
        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div>
            {/* Search Form */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <form onSubmit={handleSearch} className="flex">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search questions..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button 
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-r-md hover:bg-indigo-700"
                >
                  Search
                </button>
              </form>
            </div>
            
            {/* Questions List */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
              <h2 className="text-lg font-medium mb-4">Student Questions</h2>
              
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              )}
              
              {!loading && questions.length === 0 && (
                <p className="text-center py-8 text-gray-500">No questions found. Try adjusting your search or time range.</p>
              )}
              
              {!loading && questions.length > 0 && (
                <div className="space-y-4">
                  {questions.map((q) => (
                    <div key={q.id} className="border rounded-lg overflow-hidden">
                      <div 
                        className={`p-4 cursor-pointer ${selectedQuestion?.id === q.id ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'}`}
                        onClick={() => handleQuestionClick(q)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-md font-medium">
                              {q.question}
                              {q.language === 'es' && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Spanish</span>
                              )}
                            </h3>
                            
                            {/* Display translated question for Spanish questions */}
                            {q.language === 'es' && q.translated_question && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">English:</span> {q.translated_question}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {new Date(q.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      
                      {selectedQuestion?.id === q.id && (
                        <div className="p-4 bg-gray-50 border-t">
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Response:</h4>
                            <div className="p-3 bg-white rounded border prose prose-sm max-w-none">
                              {q.language === 'es' ? (
                                <>
                                  {q.full_response.split(/---+/).map((part, index) => (
                                    <div key={index} className={index > 0 ? "mt-4 pt-4 border-t border-gray-200" : ""}>
                                      {index === 0 && <div className="mb-1 text-xs text-blue-600 font-medium">Spanish Response:</div>}
                                      {index === 1 && <div className="mb-1 text-xs text-blue-600 font-medium">English Translation:</div>}
                                      <ReactMarkdown>{part.trim()}</ReactMarkdown>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <ReactMarkdown>
                                  {q.full_response || q.response_summary}
                                </ReactMarkdown>
                              )}
                            </div>
                          </div>
                          
                          {q.citations && q.citations.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Curriculum Sources:</h4>
                              <ul className="list-disc pl-5 text-sm">
                                {q.citations.map((citation, idx) => (
                                  <li key={idx} className="text-indigo-600">
                                    {citation.filename}
                                    {citation.section && <span> - {citation.section}</span>}
                                    {citation.page && <span> (Page {citation.page})</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="mt-3 flex justify-end">
                            <button 
                              className="text-xs text-indigo-600 hover:text-indigo-800"
                              onClick={() => setSelectedQuestion(null)}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                  <nav className="inline-flex rounded-md shadow">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-l-md border ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <div className="px-4 py-1 bg-white border-t border-b text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-r-md border ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {/* Analytics Cards */}
            {analytics && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-medium mb-2">Total Questions</h3>
                  <p className="text-3xl font-bold text-indigo-600">{analytics.total_questions}</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-medium mb-2">Top Topic</h3>
                  <p className="text-3xl font-bold text-indigo-600">
                    {analytics.topic_distribution && analytics.topic_distribution.length > 0 
                      ? analytics.topic_distribution[0].topic 
                      : 'N/A'}
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-medium mb-2">Questions per Day</h3>
                  <p className="text-3xl font-bold text-indigo-600">
                    {(analytics.total_questions / timeframe).toFixed(1)}
                  </p>
                </div>
              </div>
            )}
            
            {/* Language Distribution */}
            {analytics && analytics.language_distribution && (
              <div className="bg-white p-4 rounded-lg shadow mb-6">
                <h3 className="text-lg font-medium mb-2">Language Distribution</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex-1 bg-gray-100 rounded-full h-8">
                    {analytics.language_distribution.en > 0 && (
                      <div 
                        className="bg-indigo-600 rounded-l-full h-8 flex items-center justify-start pl-4 text-white text-sm font-medium"
                        style={{ 
                          width: `${analytics.language_distribution.en / analytics.total_questions * 100}%`,
                          minWidth: '40px'
                        }}
                      >
                        {analytics.language_distribution.en}
                      </div>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">English</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-8">
                    {analytics.language_distribution.es > 0 && (
                      <div 
                        className="bg-blue-600 rounded-l-full h-8 flex items-center justify-start pl-4 text-white text-sm font-medium"
                        style={{ 
                          width: `${analytics.language_distribution.es / analytics.total_questions * 100}%`,
                          minWidth: '40px'
                        }}
                      >
                        {analytics.language_distribution.es}
                      </div>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Spanish</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Topic Distribution Chart */}
            {analytics && analytics.topic_distribution && analytics.topic_distribution.length > 0 && (
              <AbsoluteBarChart
                title="Topic Distribution"
                data={analytics.topic_distribution}
                getLabel={item => item.topic}
                getValue={item => item.count}
              />
            )}
            
            {/* Daily Trend Chart */}
            {analytics && analytics.question_count_by_day && analytics.question_count_by_day.length > 0 && (
              <AbsoluteBarChart
                title="Daily Question Trend"
                data={analytics.question_count_by_day}
                getLabel={item => new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                getValue={item => item.count}
              />
            )}
          </div>
        )}
        
        {/* Curriculum Gaps Tab */}
        {activeTab === 'gaps' && (
          <div>
            {/* Search Form */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <form onSubmit={(e) => {
                e.preventDefault();
                loadCurriculumGaps();
              }} className="flex">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search curriculum gaps..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button 
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-r-md hover:bg-indigo-700"
                >
                  Search
                </button>
              </form>
            </div>
            
            {/* Curriculum Gaps List */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="text-lg font-medium mb-4">Potential Curriculum Gaps</h2>
              
              {gapsLoading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              )}
              
              {!gapsLoading && curriculumGaps.length === 0 && (
                <p className="text-center py-8 text-gray-500">No curriculum gaps identified. This could mean your curriculum is comprehensive or there aren't enough questions yet to identify gaps.</p>
              )}
              
              {!gapsLoading && curriculumGaps.length > 0 && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-4">
                    These questions may not be well-covered by the current curriculum. They either received short responses or had no citations to curriculum materials.
                  </p>
                  
                  {curriculumGaps.map((gap) => (
                    <div key={gap.id} className="border rounded-lg p-4 bg-pink-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-md font-medium">
                            {gap.question}
                            {gap.language === 'es' && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Spanish</span>
                            )}
                          </h3>
                          
                          {/* Display translated question for Spanish questions */}
                          {gap.language === 'es' && gap.translated_question && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">English:</span> {gap.translated_question}
                            </p>
                          )}
                          
                          <div className="mt-2 flex space-x-4 text-xs">
                            <span className={`px-2 py-1 rounded-full ${gap.has_citations ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {gap.has_citations ? 'Limited Citations' : 'No Citations'}
                            </span>
                            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                              Response Length: {gap.response_length} chars
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InstructorDashboard;